import { AiTag, NewTag } from '../tag';
import { HelpIcon } from '../helpIconContainer';
import { Icon } from '../icon';
import { Link } from '../buttons';
import { ZTooltip } from '../ztooltip';
import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useRelayStore } from 'src/relay';
import { getPathName } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { GetIcon } from './IconManager';
import { IsSubMenu, ItemMenu } from './Menu';
import { MenuItemsProps } from './TreeMenu';
import { T, isMenuItemSelected } from './helpers';
import { ContainerArrow, ContainerIcon, ContainerTooltip, LinkSidebar, Navbar, NavbarContainer, NavbarLi } from './leftAppSidebarStyles';

export const SelectedMenuItem = styled.span`
    color: ${theme.app.secondary};
`;

const isAllowed = (iamUserPermissions: string[], iamUser: boolean, permissionsToHave: string[]) => {
    if (!iamUser || permissionsToHave.length === 0) {
        return true;
    }

    return permissionsToHave.every((p) => iamUserPermissions.includes(p));
};

const MenuList = (itemMenu: ItemMenu, goToMenu: (item: ItemMenu) => any, iamUser: boolean, iamUserPermissions: string[]) => {
    return itemMenu.menu.map((item, i) => <div key={i}>{ContainerLi(item, false, goToMenu, !isAllowed(iamUserPermissions, iamUser, item.permissions))}</div>);
};

const ContainerLi = (itemMenu: ItemMenu, isHeader: boolean, goToMenu: (item: ItemMenu) => any, disabled?: boolean) => {
    const [isDirty, incrementStoreKey] = useRelayStore((state) => [state.isRelayStoreDirty, state.incrementRelayStoreKey]);
    const navigate = useNavigate();

    const intelNav = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        if (!isHeader && IsSubMenu(itemMenu) && e.currentTarget.target !== '_blank') {
            goToMenu(itemMenu);
            return;
        }

        // Emit custom event to allow other components to intercept navigation
        const navigationEvent = new CustomEvent('app-navigate-request', {
            detail: {
                path: getPathName(itemMenu.urlPage),
                urlPage: itemMenu.urlPage,
                isDam: window.location.href.toLowerCase().includes('dam')
            },
            cancelable: true
        });

        const allowed = window.dispatchEvent(navigationEvent);

        // If the event was not prevented, proceed with navigation
        if (allowed) {
            if (isDirty) incrementStoreKey();
            if (window.location.href.toLowerCase().includes('dam')) window.open(itemMenu.urlPage, '_self');
            else navigate(getPathName(itemMenu.urlPage));
        }
    };

    const handleAuxClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (e.button == 1) {
            // Middle click
            e.preventDefault();
            window.open(itemMenu.urlPage, '_blank');
            return;
        }
    };

    const isSelected = isMenuItemSelected(itemMenu);

    return (
        <NavbarLi $isVisible={itemMenu.name.toLowerCase() !== 'dam'}>
            <LinkSidebar
                href={isHeader ? 'javascript:void(0)' : itemMenu.urlPage}
                $isHeader={isHeader}
                onClick={intelNav}
                onAuxClick={handleAuxClick}
                target={itemMenu.isExternalUrl ? '_blank' : '_self'}
            >
                <ContainerIcon $isSelected={isSelected}>{GetIcon(itemMenu.icon) && React.createElement(GetIcon(itemMenu.icon)!)}</ContainerIcon>

                {isSelected ? <SelectedMenuItem>{itemMenu.name}</SelectedMenuItem> : <span>{itemMenu.name}</span>}

                {itemMenu.isAI && <AiTag>AI</AiTag>}
                {itemMenu.isNew && <NewTag>NEW</NewTag>}
                {itemMenu.tooltip && (
                    <ContainerTooltip>
                        <ZTooltip
                            interactive
                            exitDelay={0}
                            addContainer
                            position='right'
                            html={
                                <label>
                                    <span dangerouslySetInnerHTML={{ __html: itemMenu.tooltip }} />

                                    {itemMenu.learnMore && (
                                        <Link target='_blank' href={itemMenu.learnMore} onClick={(e) => e.stopPropagation()}>
                                            <br />
                                            {T._('Learn more', 'Admin')}
                                        </Link>
                                    )}
                                </label>
                            }
                        >
                            <HelpIcon />
                        </ZTooltip>
                    </ContainerTooltip>
                )}
                <ContainerArrow $isHidden={IsSubMenu(itemMenu) && !isHeader}>
                    <Icon>{GetIcon('angle_right_solid') && React.createElement(GetIcon('angle_right_solid')!)}</Icon>
                </ContainerArrow>
            </LinkSidebar>
        </NavbarLi>
    );
};

export const BackContainer = styled.div`
    cursor: pointer;
    padding: 5px;
    display: flex;
    align-items: center;
    gap: 10px;

    &:hover {
        opacity: 0.7;
    }

    svg {
        width: 24px;
        height: 24px;
    }
`;

const MenuItems: FunctionComponent<MenuItemsProps> = (props) => {
    return (
        <TransitionGroup component={NavbarContainer}>
            <CSSTransition key={props.currentMenu.urlPage} timeout={300} classNames='transition'>
                <Navbar>
                    {props.currentMenu.name !== 'HOME' && (
                        <BackContainer onClick={props.onBackClick}>
                            <Icon>{React.createElement(GetIcon('arrow_left_solid')!)}</Icon>

                            <strong>{props.homeMenu.name}</strong>
                        </BackContainer>
                    )}

                    {MenuList(props.currentMenu, props.goToMenu, props.iamUser, props.iamUserPermissions)}
                </Navbar>
            </CSSTransition>
        </TransitionGroup>
    );
};

export default MenuItems;
